import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Star, ShieldWarning, CheckCircle, DownloadSimple } from "@phosphor-icons/react";

export default function Temporadas() {
  const [seasons, setSeasons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", is_current: false });
  const [purge, setPurge] = useState(null); // { season, step, preview, security_key, typed }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/seasons");
      setSeasons(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar las temporadas");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/seasons", {
        name: form.name.trim(),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        is_current: !!form.is_current,
      });
      toast.success("Temporada creada");
      setShowForm(false);
      setForm({ name: "", start_date: "", end_date: "", is_current: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al crear la temporada");
    }
  };

  const activate = async (id) => {
    try {
      await api.post(`/seasons/${id}/activate`);
      toast.success("Temporada activada");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const updateSeason = async (id, patch) => {
    try {
      await api.patch(`/seasons/${id}`, patch);
      toast.success("Temporada actualizada");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteSeason = async (s) => {
    if (!window.confirm(`¿Eliminar la temporada "${s.name}"? Sólo se puede si NO tiene datos asociados.`)) return;
    try {
      await api.delete(`/seasons/${s.id}`);
      toast.success("Temporada eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const backup = async (s) => {
    try {
      const { data, headers } = await api.get(`/seasons/${s.id}/backup.zip`, { responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const match = (headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : `backup_${s.name}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Copia de seguridad descargada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el backup");
    }
  };

  const openPurge = async (s) => {
    try {
      const { data } = await api.get(`/seasons/${s.id}/purge-preview`);
      setPurge({ season: s, step: 1, preview: data, security_key: "", typed: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="temporadas-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase text-[#007AFF] font-bold">Administración</div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tighter">Temporadas</h1>
          <div className="text-sm text-slate-500 mt-1">Gestión de temporadas deportivas · Sólo administradores</div>
        </div>
        <button
          data-testid="add-season-btn"
          onClick={() => setShowForm(true)}
          className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 flex items-center gap-2 font-display font-bold uppercase tracking-wide text-sm"
          style={{ borderRadius: 6 }}
        >
          <Plus size={16} weight="bold" /> Nueva temporada
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white border border-slate-200 shadow-card overflow-hidden" style={{ borderRadius: 12 }}>
        {seasons.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Aún no hay temporadas. Crea la primera con el botón de arriba.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3 font-bold">Nombre</th>
                <th className="px-4 py-3 font-bold">Inicio</th>
                <th className="px-4 py-3 font-bold">Fin</th>
                <th className="px-4 py-3 font-bold">Estado</th>
                <th className="px-4 py-3 font-bold text-right">Partidos</th>
                <th className="px-4 py-3 font-bold text-right">Ingresos</th>
                <th className="px-4 py-3 font-bold text-right">Gastos</th>
                <th className="px-4 py-3 font-bold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {seasons.map((s) => (
                <tr key={s.id} className={`border-b border-slate-100 ${s.is_current ? "bg-emerald-50/40" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {s.is_current && <Star size={16} weight="fill" className="text-emerald-500" />}
                      <span className="font-display font-bold uppercase tracking-tight">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <input
                      type="date"
                      defaultValue={s.start_date || ""}
                      onBlur={(e) => e.target.value !== (s.start_date || "") && updateSeason(s.id, { start_date: e.target.value || null })}
                      className="bg-slate-50 border border-slate-200 px-2 h-8 text-xs focus:border-[#2563EB] focus:bg-white outline-none"
                      style={{ borderRadius: 4 }}
                      data-testid={`season-start-${s.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <input
                      type="date"
                      defaultValue={s.end_date || ""}
                      onBlur={(e) => e.target.value !== (s.end_date || "") && updateSeason(s.id, { end_date: e.target.value || null })}
                      className="bg-slate-50 border border-slate-200 px-2 h-8 text-xs focus:border-[#2563EB] focus:bg-white outline-none"
                      style={{ borderRadius: 4 }}
                      data-testid={`season-end-${s.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <select
                      defaultValue={s.status}
                      onChange={(e) => updateSeason(s.id, { status: e.target.value })}
                      className="bg-slate-50 border border-slate-200 px-2 h-8 text-xs focus:border-[#2563EB] focus:bg-white outline-none capitalize"
                      style={{ borderRadius: 4 }}
                      data-testid={`season-status-${s.id}`}
                    >
                      <option value="activa">Activa</option>
                      <option value="cerrada">Cerrada</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right font-display font-bold">{s.counts.matches}</td>
                  <td className="px-4 py-3 text-right text-emerald-600 font-display font-bold">{s.counts.ingresos}</td>
                  <td className="px-4 py-3 text-right text-red-600 font-display font-bold">{s.counts.gastos}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!s.is_current && (
                        <button
                          data-testid={`activate-season-${s.id}`}
                          onClick={() => activate(s.id)}
                          title="Marcar como temporada activa"
                          className="border border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 px-2 h-8 text-[11px] font-display font-bold uppercase tracking-widest"
                          style={{ borderRadius: 4 }}
                        >
                          Activar
                        </button>
                      )}
                      <button
                        data-testid={`backup-season-${s.id}`}
                        onClick={() => backup(s)}
                        title="Descargar copia de seguridad (ZIP) de esta temporada"
                        className="border border-blue-300 hover:border-blue-500 hover:bg-blue-50 text-blue-700 px-2 h-8 text-[11px] font-display font-bold uppercase tracking-widest flex items-center gap-1"
                        style={{ borderRadius: 4 }}
                      >
                        <DownloadSimple size={12} weight="bold" /> Backup
                      </button>
                      <button
                        data-testid={`purge-season-${s.id}`}
                        onClick={() => openPurge(s)}
                        title="Eliminar datos de esta temporada (partidos, ingresos, gastos)"
                        className="border border-red-300 hover:border-red-500 hover:bg-red-50 text-red-700 px-2 h-8 text-[11px] font-display font-bold uppercase tracking-widest flex items-center gap-1"
                        style={{ borderRadius: 4 }}
                      >
                        <ShieldWarning size={12} weight="bold" /> Purgar
                      </button>
                      <button
                        data-testid={`delete-season-${s.id}`}
                        onClick={() => deleteSeason(s)}
                        title="Eliminar el registro de la temporada (sólo si no tiene datos)"
                        className="border border-slate-300 hover:border-red-500 hover:bg-red-50 text-slate-500 hover:text-red-700 px-2 h-8"
                        style={{ borderRadius: 4 }}
                      >
                        <Trash size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal crear */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form
            onSubmit={create}
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-6 w-full max-w-md space-y-4"
            style={{ borderRadius: 12 }}
            data-testid="new-season-modal"
          >
            <h3 className="font-display font-bold text-2xl uppercase tracking-tight">Nueva temporada</h3>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Nombre *</label>
              <input
                data-testid="new-season-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="p.ej. 2027-28 o 2027/2028"
                className="w-full bg-slate-50 border border-slate-200 px-3 h-11 focus:border-[#2563EB] focus:bg-white outline-none mt-1"
                style={{ borderRadius: 6 }}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Inicio</label>
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 px-3 h-11 focus:border-[#2563EB] focus:bg-white outline-none mt-1"
                  style={{ borderRadius: 6 }} />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Fin</label>
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-200 px-3 h-11 focus:border-[#2563EB] focus:bg-white outline-none mt-1"
                  style={{ borderRadius: 6 }} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.is_current} onChange={(e) => setForm({ ...form, is_current: e.target.checked })} className="h-4 w-4 accent-emerald-600" />
              Marcar como temporada activa
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 h-11 border border-slate-200 hover:border-zinc-500" style={{ borderRadius: 6 }}>Cancelar</button>
              <button type="submit" className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-5 h-11 font-display font-bold uppercase tracking-wide" style={{ borderRadius: 6 }}>Crear</button>
            </div>
          </form>
        </div>
      )}

      {/* Modal purga */}
      {purge && <PurgeModal state={purge} setState={setPurge} onDone={load} />}
    </div>
  );
}

function PurgeModal({ state, setState, onDone }) {
  const { season, step, preview, security_key, typed } = state;
  const expectedKey = `BorrarTemporada${season.name}`;
  const expectedConf = `ELIMINAR ${season.name}`;
  const canConfirm = security_key.trim() === expectedKey && typed.trim() === expectedConf;

  const downloadBackup = async () => {
    try {
      const { data, headers } = await api.get(`/seasons/${season.id}/backup.zip`, { responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const match = (headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : `backup_${season.name}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Copia de seguridad descargada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const execute = async () => {
    try {
      const { data } = await api.post(`/seasons/${season.id}/purge`, {
        security_key: security_key.trim(),
        typed_confirmation: typed.trim(),
      });
      toast.success(`Purga completada: ${data.deleted.matches} partidos, ${data.deleted.transactions} movimientos, ${data.deleted.accesses} accesos`);
      setState(null);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="purge-modal">
      <div
        className="bg-white w-full max-w-lg p-6 space-y-5 border-2 border-red-500"
        style={{ borderRadius: 16 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
            <ShieldWarning size={26} weight="fill" className="text-red-600" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-red-600 font-bold">Operación irreversible</div>
            <h3 className="font-display font-bold text-2xl uppercase tracking-tight">Eliminar datos de temporada</h3>
          </div>
        </div>

        {season.is_current && (
          <div className="bg-orange-50 border border-orange-300 text-orange-900 px-4 py-3 text-sm" style={{ borderRadius: 8 }}>
            <strong>Es la temporada activa.</strong> Activa otra temporada antes de purgar sus datos.
          </div>
        )}

        <div className="bg-slate-50 border border-slate-200 px-4 py-4 space-y-2" style={{ borderRadius: 8 }}>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">Vas a eliminar los datos de:</div>
          <div className="font-display font-black text-3xl uppercase tracking-tight">{season.name}</div>
          <ul className="text-sm text-slate-700 mt-2 space-y-0.5">
            <li>· <strong>{preview.counts.matches}</strong> partidos</li>
            <li>· <strong>{preview.counts.ingresos}</strong> ingresos</li>
            <li>· <strong>{preview.counts.gastos}</strong> gastos</li>
            <li>· <strong>{preview.counts.accesses}</strong> registros de acceso vinculados</li>
          </ul>
          <div className="text-xs text-emerald-700 font-bold mt-2 flex items-center gap-1">
            <CheckCircle size={13} weight="bold" /> Los socios NO serán eliminados
          </div>
          <button
            data-testid="purge-backup-btn"
            onClick={downloadBackup}
            className="mt-3 w-full border border-blue-400 hover:border-blue-600 hover:bg-blue-50 text-blue-700 px-3 h-10 flex items-center justify-center gap-2 text-xs font-display font-bold uppercase tracking-widest transition"
            style={{ borderRadius: 6 }}
          >
            <DownloadSimple size={14} weight="bold" /> Descarga primero copia de seguridad (ZIP)
          </button>
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">1. Clave de seguridad</label>
              <input
                data-testid="purge-key-input"
                type="password"
                value={security_key}
                onChange={(e) => setState({ ...state, security_key: e.target.value })}
                placeholder="BorrarTemporada+nombre de la temporada"
                className="w-full bg-slate-50 border border-slate-200 px-3 h-11 focus:border-red-500 focus:bg-white outline-none mt-1"
                style={{ borderRadius: 6 }}
                autoFocus
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Pista: comienza por <code className="bg-slate-100 px-1">BorrarTemporada</code> seguido del nombre exacto de la temporada.
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold">2. Confirmación escrita</label>
              <input
                data-testid="purge-typed-input"
                value={typed}
                onChange={(e) => setState({ ...state, typed: e.target.value })}
                placeholder={expectedConf}
                className="w-full bg-slate-50 border border-slate-200 px-3 h-11 focus:border-red-500 focus:bg-white outline-none mt-1 font-mono"
                style={{ borderRadius: 6 }}
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Escribe exactamente: <code className="bg-slate-100 px-1 font-bold">{expectedConf}</code>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          <button
            onClick={() => setState(null)}
            className="px-4 h-11 border border-slate-200 hover:border-zinc-500"
            style={{ borderRadius: 6 }}
          >
            Cancelar
          </button>
          <button
            data-testid="purge-execute-btn"
            disabled={!canConfirm || season.is_current}
            onClick={execute}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 h-11 font-display font-bold uppercase tracking-wide flex items-center gap-2"
            style={{ borderRadius: 6 }}
          >
            <Trash size={16} weight="bold" /> Eliminar datos de esta temporada
          </button>
        </div>
      </div>
    </div>
  );
}
